import React from 'react'
import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import LoginPage from './pages/LoginPage'
import ProtectedRoute from './components/ProtectedRoute'
import ViewerGuard from './components/ViewerGuard'
import Layout from './components/Layout'
import CommunityLayout from './components/CommunityLayout'
import Dashboard from './pages/Dashboard'
import ScenarioList from './pages/ScenarioList'
import ScenarioWorkspace from './pages/ScenarioWorkspace'
import CommunityListPage from './pages/CommunityListPage'
import CommunitySetupPage from './pages/CommunitySetupPage'
import CommunityInitWizard from './pages/CommunityInitWizard'
import AssetsSetupPage from './pages/AssetsSetupPage'
import BuildingsSetupPage from './pages/BuildingsSetupPage'
import DatasetUploadPage from './pages/DatasetUploadPage'
import InitializationWizard from './pages/InitializationWizard'
import CommunityDashboard from './pages/CommunityDashboard'
import ForecastingPage from './pages/ForecastingPage'
import ModellingPage from './pages/ModellingPage'
import PlanningToolsPage from './pages/PlanningToolsPage'
import PlanningSetupPage from './pages/PlanningSetupPage'
import PlanningResultsPage from './pages/PlanningResultsPage'
import PastResultsPage from './pages/PastResultsPage'
import Documentation from './pages/Documentation'
import NotFound from './pages/NotFound'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo })
    console.error('ErrorBoundary caught:', error, errorInfo)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', color: 'red' }}>
          <h2>Something went wrong</h2>
          <p><strong>Error:</strong> {this.state.error?.message}</p>
          <h3>JS Stack:</h3>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', background: '#fff0f0', padding: '1rem', maxHeight: '300px', overflow: 'auto' }}>{this.state.error?.stack}</pre>
          <h3>Component Stack:</h3>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.75rem', background: '#fff0f0', padding: '1rem', maxHeight: '300px', overflow: 'auto' }}>{this.state.errorInfo?.componentStack}</pre>
          <button onClick={() => { this.setState({ hasError: false, error: null, errorInfo: null }); window.location.reload(); }}>Reload</button>
        </div>
      )
    }
    return this.props.children
  }
}

function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <Layout>
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<Dashboard />} />

                  {/* Documentation / Help Center */}
                  <Route path="/documentation" element={<Documentation />} />

                  {/* Planning Tools (renamed from Scenarios) */}
                  <Route path="/planning" element={<PlanningToolsPage />} />
                  <Route path="/planning/new" element={<ViewerGuard redirectTo="/planning"><PlanningSetupPage /></ViewerGuard>} />
                  <Route path="/planning/:id/edit" element={<ViewerGuard redirectTo="/planning"><PlanningSetupPage /></ViewerGuard>} />
                  <Route path="/planning/:id/results" element={<PlanningResultsPage />} />

                  {/* Legacy Scenario Routes (preserved for backward compatibility) */}
                  <Route path="/scenarios" element={<ScenarioList />} />
                  <Route path="/scenarios/:id/edit" element={<ViewerGuard redirectTo="/scenarios"><ScenarioWorkspace /></ViewerGuard>} />

                  {/* Community List, New Community Setup, and Edit (no sidebar) */}
                  <Route path="/communities" element={<CommunityListPage />} />
                  <Route path="/communities/new" element={<ViewerGuard redirectTo="/communities"><CommunityInitWizard /></ViewerGuard>} />
                  <Route path="/communities/:id/edit" element={<ViewerGuard redirectTo="/communities"><CommunityInitWizard /></ViewerGuard>} />

                  {/* Legacy routes - kept for backward compatibility but redirect to wizard */}
                  <Route path="/communities/new/assets" element={<ViewerGuard redirectTo="/communities"><CommunityInitWizard /></ViewerGuard>} />
                  <Route path="/communities/new/buildings" element={<ViewerGuard redirectTo="/communities"><CommunityInitWizard /></ViewerGuard>} />
                  <Route path="/communities/new/data" element={<ViewerGuard redirectTo="/communities"><CommunityInitWizard /></ViewerGuard>} />
                  <Route path="/communities/new/review" element={<ViewerGuard redirectTo="/communities"><CommunityInitWizard /></ViewerGuard>} />

                  {/*
                    Community-specific pages with sidebar layout.
                    CommunityLayout provides: sidebar navigation + community data via Outlet context.
                    Child routes access community data via useOutletContext().
                  */}
                  <Route path="/communities/:id" element={<CommunityLayout />}>
                    {/* Default: redirect to dashboard */}
                    <Route index element={<CommunityDashboard />} />

                    {/* Community Dashboard - per-community KPIs, charts, quick actions */}
                    <Route path="dashboard" element={<CommunityDashboard />} />

                    {/* Energy Tools */}
                    <Route path="forecasting" element={<ForecastingPage />} />
                    <Route path="modelling" element={<ModellingPage />} />

                    {/* Management Pages */}
                    <Route path="assets" element={<AssetsSetupPage />} />
                    <Route path="buildings" element={<BuildingsSetupPage />} />
                    <Route path="data" element={<DatasetUploadPage />} />

                    {/* Results Pages */}
                    <Route path="results" element={<PastResultsPage />} />
                  </Route>

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </ErrorBoundary>
            </Layout>
          </ProtectedRoute>
        } />
      </Routes>
    </AuthProvider>
  )
}

export default App
